import type { JoinRoomResult, RoomLayout, TalkRoom } from "../../lib/types";
import { talkBrand } from "../../lib/brand";

export interface StoredRoomSession {
  result: JoinRoomResult;
  media: {
    cameraEnabled: boolean;
    microphoneEnabled: boolean;
    cameraDeviceId: string;
    microphoneDeviceId: string;
    speakerDeviceId: string;
    reducedData: boolean;
    defaultLayout: RoomLayout;
  };
}

export interface ReconnectMetadata {
  room: Omit<TalkRoom, "code">;
  participant: Pick<JoinRoomResult["participant"], "id" | "displayName" | "role" | "guest" | "assetKey">;
  media: StoredRoomSession["media"];
  savedAt: string;
}

const activeSessions = new Map<string, StoredRoomSession>();
const prejoinTickets = new Map<string, string>();

function reconnectKey(roomId: string): string {
  return `${talkBrand.storagePrefix}:talk-reconnect:${roomId}`;
}

export function savePrejoinTicket(roomId: string, ticket: string): void {
  if (roomId && ticket) prejoinTickets.set(roomId, ticket);
}

export function loadPrejoinTicket(roomId: string): string {
  return prejoinTickets.get(roomId) ?? "";
}

export function clearPrejoinTicket(roomId: string): void {
  prejoinTickets.delete(roomId);
}

export function loadRoomSession(roomId: string): StoredRoomSession | null {
  const session = activeSessions.get(roomId);
  if (!session?.result.serverUrl || !session.result.participantTicket) return null;
  if (session.result.room.mode !== "private_1to1" && !session.result.rtcToken) return null;
  return session;
}

export function loadReconnectMetadata(roomId: string): ReconnectMetadata | null {
  const active = activeSessions.get(roomId);
  if (active) return reconnectMetadata(active);
  try {
    const parsed = JSON.parse(window.localStorage.getItem(reconnectKey(roomId)) ?? "null") as ReconnectMetadata | null;
    if (parsed?.room?.id !== roomId || !parsed.participant?.id || !parsed.savedAt || !parsed.media) return null;
    return parsed;
  } catch {
    return null;
  }
}

function reconnectMetadata(session: StoredRoomSession): ReconnectMetadata {
  const { code: _code, ...room } = session.result.room;
  void _code;
  return {
    room,
    participant: {
      id: session.result.participant.id,
      displayName: session.result.participant.displayName,
      role: session.result.participant.role,
      guest: session.result.participant.guest,
      ...(session.result.participant.assetKey ? { assetKey: session.result.participant.assetKey } : {}),
    },
    media: session.media,
    savedAt: new Date().toISOString(),
  };
}

export function saveRoomSession(session: StoredRoomSession): void {
  const roomId = session.result.room.id;
  activeSessions.set(roomId, session);
  const reconnect = reconnectMetadata(session);
  try { window.localStorage.setItem(reconnectKey(roomId), JSON.stringify(reconnect)); } catch {
    // Storage denial must not interrupt a live call or discard fresh in-memory credentials.
  }
}

export function clearActiveRoomSession(roomId: string): void {
  activeSessions.delete(roomId);
}

export function clearRoomSession(roomId: string): void {
  activeSessions.delete(roomId);
  prejoinTickets.delete(roomId);
  try { window.localStorage.removeItem(reconnectKey(roomId)); } catch { /* Storage may be unavailable. */ }
}
